"use client";

import Link from "next/link";
import { UsaliVisual } from "@/components/landing/ModuleVisuals";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n/I18nProvider";

interface Schedule {
  num: string;
  name: string;
  descKey: string;
}

export default function UsaliProductPage() {
  const { t } = useI18n();

  const schedules: Schedule[] = [
    {
      num: "S1",
      name: "Profit & Loss",
      descKey: "pages.product.usali.schedules.s1.desc",
    },
    {
      num: "S2",
      name: "Balance Sheet",
      descKey: "pages.product.usali.schedules.s2.desc",
    },
    {
      num: "S3",
      name: "Cash Flow",
      descKey: "pages.product.usali.schedules.s3.desc",
    },
    {
      num: "S4",
      name: "Key Statistics",
      descKey: "pages.product.usali.schedules.s4.desc",
    },
    {
      num: "S5-S6",
      name: "Revenue & Expenses",
      descKey: "pages.product.usali.schedules.s5.desc",
    },
    {
      num: "S7-S10",
      name: "Department Schedules",
      descKey: "pages.product.usali.schedules.s7.desc",
    },
    {
      num: "S11-S12",
      name: "Support Schedules",
      descKey: "pages.product.usali.schedules.s11.desc",
    },
    {
      num: "S13-S14",
      name: "Financial Analysis",
      descKey: "pages.product.usali.schedules.s13.desc",
    },
  ];

  const benefits = [
    {
      titleKey: "pages.product.usali.benefits.mapping.title",
      descKey: "pages.product.usali.benefits.mapping.desc",
    },
    {
      titleKey: "pages.product.usali.benefits.automation.title",
      descKey: "pages.product.usali.benefits.automation.desc",
    },
    {
      titleKey: "pages.product.usali.benefits.budgeting.title",
      descKey: "pages.product.usali.benefits.budgeting.desc",
    },
    {
      titleKey: "pages.product.usali.benefits.comparison.title",
      descKey: "pages.product.usali.benefits.comparison.desc",
    },
    {
      titleKey: "pages.product.usali.benefits.export.title",
      descKey: "pages.product.usali.benefits.export.desc",
    },
    {
      titleKey: "pages.product.usali.benefits.audit.title",
      descKey: "pages.product.usali.benefits.audit.desc",
    },
  ];

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
              {t("pages.product.usali.headline") ||
                "USALI 12th Edition. По умолчанию."}
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              {t("pages.product.usali.description") ||
                `Полная поддержка USALI 12 (Uniform System of Accounts for the
                Lodging Industry). 14 расписаний, автоматическое маппирование
                счетов из 1С, бюджетное планирование и анализ.`}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild variant="default" className="h-12">
                <Link href="/contact">
                  {t("pages.product.usali.cta") || "Запросить демо"}
                </Link>
              </Button>
              <Button asChild variant="outline" className="h-12">
                <Link href="/pricing">
                  {t("pages.product.usali.pricing") || "Смотреть тарифы"}
                </Link>
              </Button>
            </div>
          </div>

          {/* Visual */}
          <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden h-96">
            <UsaliVisual />
          </div>
        </div>
      </section>

      {/* What is USALI */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              {t("pages.product.usali.whatTitle") || "Что такое USALI?"}
            </h2>
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              {t("pages.product.usali.whatDesc1") ||
                `USALI (Uniform System of Accounts for the Lodging Industry) —
                это стандартная система бухгалтерского учёта для отелей,
                разработанная American Hotel & Lodging Association.`}
            </p>
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              {t("pages.product.usali.whatDesc2") ||
                `USALI 12 (12-е издание) включает 14 расписаний: Profit & Loss,
                Balance Sheet, Cash Flow, KPI и много другого. Это стандарт
                для серьёзного отельного бизнеса в России и мире.`}
            </p>
            <div className="bg-slate-50 rounded-lg p-6 border border-slate-200">
              <p className="text-sm text-slate-600">
                {t("pages.product.usali.whyUsali") ||
                  `Почему USALI? Потому что это единственный стандарт,
                  который позволяет сравнивать результаты разных отелей,
                  привлекать инвесторов и масштабировать сеть.`}
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-xl font-bold text-slate-900 mb-6">
              {t("pages.product.usali.schedulesTitle") ||
                "14 расписаний USALI"}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {schedules.map((sch) => (
                <div
                  key={sch.num}
                  className="border border-slate-200 rounded-lg p-4 hover:border-keiro-green/50 transition-colors"
                >
                  <div className="flex items-start gap-3 mb-2">
                    <span className="text-sm font-bold text-keiro-green-dark bg-keiro-mist rounded px-2 py-1">
                      {sch.num}
                    </span>
                  </div>
                  <p className="font-semibold text-sm text-slate-900 mb-1">
                    {sch.name}
                  </p>
                  <p className="text-xs text-slate-500">
                    {t(sch.descKey) || sch.descKey}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Account Mapping */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.usali.mappingTitle") ||
            "Автоматическое маппирование счетов"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              title:
                t("pages.product.usali.mapping1.title") ||
                "1С → USALI маппинг",
              desc:
                t("pages.product.usali.mapping1.desc") ||
                "Счета из 1С автоматически переводятся в структуру USALI",
              icon: "🔄",
            },
            {
              title:
                t("pages.product.usali.mapping2.title") ||
                "Конфигурируемые правила",
              desc:
                t("pages.product.usali.mapping2.desc") ||
                "Каждый отель может иметь свою схему маппирования",
              icon: "⚙️",
            },
            {
              title:
                t("pages.product.usali.mapping3.title") ||
                "Синхронизация в реальном времени",
              desc:
                t("pages.product.usali.mapping3.desc") ||
                "Данные обновляются каждый час. Всегда актуальные цифры.",
              icon: "⚡",
            },
          ].map((item, idx) => (
            <div
              key={idx}
              className="border border-slate-200 rounded-xl p-6"
            >
              <div className="text-4xl mb-4">{item.icon}</div>
              <h3 className="font-bold text-slate-900 mb-3">{item.title}</h3>
              <p className="text-slate-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12">
          {t("pages.product.usali.featuresTitle") || "Возможности"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, idx) => (
            <div key={idx} className="border border-slate-200 rounded-xl p-6">
              <div className="flex items-start gap-3 mb-4">
                <CheckCircle2 size={24} className="text-keiro-green flex-shrink-0 mt-0.5" />
                <h3 className="font-bold text-slate-900">
                  {t(benefit.titleKey) || benefit.titleKey}
                </h3>
              </div>
              <p className="text-slate-600">
                {t(benefit.descKey) || benefit.descKey}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Benchmarking */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-8">
            <p className="text-slate-600 text-center">
              {t("pages.product.usali.benchmarkingPlaceholder") ||
                "Интерактивный бенчмарк"}
            </p>
          </div>
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              {t("pages.product.usali.benchmarkingTitle") ||
                "Сравнивайте себя с другими"}
            </h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              {t("pages.product.usali.benchmarkingDesc") ||
                `С USALI данными легко сравнить свой отель с аналогичными.
                RevPAR, GOPPAR, занятость — всё в одном месте.`}
            </p>
            <ul className="space-y-4">
              {[
                t("pages.product.usali.bm1") ||
                  "Сравнение с отелями того же класса",
                t("pages.product.usali.bm2") || "Анализ трендов по году",
                t("pages.product.usali.bm3") || "Выявление слабых мест",
              ].map((item, idx) => (
                <li key={idx} className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-keiro-green rounded-full" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.usali.useCasesTitle") || "Когда это критично"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {[
            {
              title: t("pages.product.usali.uc1.title") || "Привлечение инвестиций",
              desc:
                t("pages.product.usali.uc1.desc") ||
                "Инвесторы требуют USALI отчёты. С Keiront это происходит автоматически.",
            },
            {
              title: t("pages.product.usali.uc2.title") || "Ипотека и кредиты",
              desc:
                t("pages.product.usali.uc2.desc") ||
                "Банки требят USALI отчёты для одобрения кредитов на развитие.",
            },
            {
              title: t("pages.product.usali.uc3.title") || "Франшиза",
              desc:
                t("pages.product.usali.uc3.desc") ||
                "Франчайзер требует стандартные отчёты от всех франчайзи. USALI это решает.",
            },
            {
              title: t("pages.product.usali.uc4.title") || "Масштабирование сети",
              desc:
                t("pages.product.usali.uc4.desc") ||
                "При 5+ отелях уже нужна унификация. USALI — единственный стандарт.",
            },
          ].map((useCase, idx) => (
            <div
              key={idx}
              className="border border-slate-200 rounded-xl p-6 hover:border-keiro-green/50 transition-colors"
            >
              <h3 className="font-bold text-slate-900 mb-3">
                {useCase.title}
              </h3>
              <p className="text-slate-600">{useCase.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-keiro-mist rounded-xl p-12 text-center border border-keiro-green/30">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            {t("pages.product.usali.ctaTitle") ||
              "Готовы к USALI?"}
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            {t("pages.product.usali.ctaDesc") ||
              "Первую P&L по USALI подготовим за 24 часа"}
          </p>
          <Button asChild variant="default" className="h-12 px-8">
            <Link href="/contact">
              {t("pages.product.usali.ctaButton") ||
                "Запросить консультацию"}
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
